import React, {useCallback, useMemo, useState} from 'react';
import {useTheme} from '@mui/material';
import {assert} from 'assert-ts';
import type {CorporationNameVariantFieldLayout} from 'components/fields/types';
import type {DataValue, NameVariantValue} from 'schemaDefinition/types';
import type {Code} from 'services/codelists/types';
import {useLocalization} from 'localization';
import {assertSimpleRequired} from 'schemaDefinition/functions/assertSimpleRequired';
import {valueAsNameVariantArray} from 'services/utils';
import {
  Layout,
  RadioButtonsField,
  SingleValueListField,
  useListOperations,
} from 'components';
import type {
  DataFormNameVariantListProps,
  CorporateNameVariantExtraField as ExtraField,
} from './types';
import {useFormSchemaGlobalScopeContext} from '../../contexts';
import {useDataFormContext} from '../contexts';
import {getBoxLabel} from '../functions';
import {
  useAddItemLabel,
  useCorporationNameVariantLabelsAndPlaceholders,
  useResolvedDataFormPartConfiguration,
} from '../hooks';
import {CorporationNameVariant} from './fields';

const nameVariantFieldLayout: CorporationNameVariantFieldLayout = {
  name: {
    layout: {
      flex: 1,
    },
  },
  subsidiary: {
    layout: {
      flex: 1,
    },
  },
  description: {
    layout: {
      flex: 1,
    },
  },
  mainForm: {
    layout: {
      flex: 1,
      alignItems: 'flex-end',
    },
  },
};

const ExtraFields: ExtraField[] = ['subsidiary', 'description'];

const anyExtraField = (value: DataValue): ExtraField | undefined => {
  const nameVariants = valueAsNameVariantArray(value) ?? [];
  return nameVariants.some(v => v.subsidiary)
    ? 'subsidiary'
    : nameVariants.some(v => v.description)
      ? 'description'
      : undefined;
};

const newVariant = (extraField: ExtraField | undefined): NameVariantValue => {
  const value: NameVariantValue = {};
  if (extraField) {
    value[extraField] = '';
  }

  return value;
};

const isExtraField = (val: string | null | undefined): val is ExtraField => {
  return val === 'subsidiary' || val === 'description';
};

export const DataFormCorporationNameVariantList: React.FC<
  DataFormNameVariantListProps
> = ({part, useValue, valuePath, mode}) => {
  const localization = useLocalization();
  const {colors} = useTheme();
  const {schema} = useFormSchemaGlobalScopeContext();
  const {id, setEditValue} = useDataFormContext();
  const {showWhenReadonlyAndEmpty, hideStructureOperations} =
    useResolvedDataFormPartConfiguration(part);
  const value = (useValue(valuePath, id, part.name) ?? null) as DataValue;

  const [extraField, setExtraField] = useState<ExtraField | undefined>(() =>
    anyExtraField(value),
  );

  const extraFieldOptions = useMemo(
    () =>
      ExtraFields.map<Code>(key => ({
        code: key,
        value:
          getBoxLabel(localization, {key: key}, schema) ??
          `Missing: boxlabel for ${key}`,
      })),
    [localization, schema],
  );
  assert(
    value === null || Array.isArray(value),
    'DataForm: "name variants"-value property must be array (or null)',
    {part: part, contextPath: valuePath, dataScope: value},
  );

  const requiredValue = useMemo(
    () =>
      !value || value.length === 0
        ? [newVariant(extraField)]
        : (value as NameVariantValue[]),
    [extraField, value],
  );

  const {onAppendValue, onDeleteValue, onMoveValue} =
    useListOperations<NameVariantValue>(
      update => {
        const nextValue = update(requiredValue);
        setEditValue(valuePath, nextValue, part);
        return nextValue;
      },
      () => newVariant(extraField),
    );

  const {contentLabels, fieldLayoutWithPlaceholders} =
    useCorporationNameVariantLabelsAndPlaceholders(
      extraField,
      nameVariantFieldLayout,
    );

  const addLabel = useAddItemLabel(part);

  const renderCustomActions = useCallback(() => {
    // const boxLabel = getBoxLabel(localization, {key: 'subsidiary'}, schema);
    const handleChangeShowExtraField = (field: string | null) => {
      const nameVariants = valueAsNameVariantArray(requiredValue);
      setExtraField(isExtraField(field) ? field : undefined);
      const newValue =
        field === 'subsidiary'
          ? nameVariants?.map<NameVariantValue>(({description: _, ...nv}) => ({
              ...nv,
              subsidiary: nv.subsidiary ?? '',
            }))
          : field === 'description'
            ? nameVariants?.map<NameVariantValue>(({subsidiary: _, ...nv}) => ({
                ...nv,
                description: nv.description ?? '',
              }))
            : nameVariants?.map<NameVariantValue>(
                ({subsidiary: _s, description: _d, ...nv}) => nv,
              );

      setEditValue(valuePath, newValue ?? null, part);
    };

    return (
      <Layout horizontal>
        <RadioButtonsField
          value={extraField ?? null}
          name={`${valuePath}-showExtraField`}
          horizontal
          onChange={handleChangeShowExtraField}
          options={extraFieldOptions}
        />
      </Layout>
    );
  }, [
    extraField,
    extraFieldOptions,
    part,
    requiredValue,
    setEditValue,
    valuePath,
  ]);

  return (
    <SingleValueListField<NameVariantValue>
      addLabel={addLabel}
      label={'.'}
      itemBackgroundColor={colors.enhancedLightBeige}
      itemPadding={1}
      contentLabels={contentLabels}
      required={assertSimpleRequired(part.required)}
      readonly={mode === 'read-only'}
      showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
      values={requiredValue}
      getValueKey={idx => `${valuePath}[${idx}]`}
      renderValue={(idx, value, focusableId) => {
        return (
          <CorporationNameVariant
            focusableId={focusableId}
            part={part}
            value={value as NameVariantValue}
            valuePath={`${valuePath}[${idx}]`}
            setFieldValue={setEditValue}
            isMainForm={idx === 0}
            mode={mode}
            showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
            layout={fieldLayoutWithPlaceholders}
          />
        );
      }}
      renderCustomActions={renderCustomActions}
      onAppendValue={
        hideStructureOperations?.includes('add') ? undefined : onAppendValue
      }
      onDeleteValue={
        hideStructureOperations?.includes('delete') ? undefined : onDeleteValue
      }
      onMoveValue={
        hideStructureOperations?.includes('move') ? undefined : onMoveValue
      }
    />
  );
};
